import React, { type Dispatch, useCallback, useState } from 'react';
import { useIsMobile } from '@mentimeter/onboarding-hooks';
import { useTranslations } from 'next-intl';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { RagnarButtonStatus } from '@mentimeter/ragnar-ui/button';
import { trackUser } from '@api/tracking/client';
import { LocalStorage } from '@mentimeter/storage';
import { FormActions } from '../../../components/FormActions';
import { StepsAndProgressBar } from '../../../components/StepsAndProgressBar';
import { OnboardingStepEnum, type StepSetup } from '../step-definitions';
import { Footer } from '../../layout/Footer';
import { Body } from '../../layout/Body';
import { EaseIn } from '../../../components/animations/EaseIn';
import {
  trackOnboardingJourneyEvent,
  trackClickedQuickActionsOption,
  TrackingContext,
} from '../../../tracking';
import type { OrganizationType } from '../../models/initial-main-usage';
import { FullScreenOnboardingPageLayout } from '../../layout/FullScreenOnboardingPageLayout';
import QuickActionsAsset from '../../../public/paper-plane-asset.png';
import type { OnboardingState } from '../../../types';
import { PersonalizedQuickActionsRadioGroup } from './PersonalizedQuickActionsRadioGroup';
import { createPresentationFromQuickActionsDefinition } from './http/create-presentation-from-quick-actions-definition';
import { VideoContent } from './components/VideoContent';
import {
  useQuickActionsOptions,
  getQuickActionVideoUrl,
} from './mappings/make-quick-actions-options';
import { ImageContent } from './components/ImageContent';

export interface PersonalisedQuickActionsProps {
  onDismiss: () => void;
  onAppliedTemplate: (existingPresentationEditPath?: string) => void;
  stepSetup: StepSetup;
  organizationType: OrganizationType;
  setOnboardingState: Dispatch<Partial<OnboardingState>>;
}

export const APPLIED_QUICK_ACTION_KEY = 'applied-quick-action';

export function PersonalizedQuickActions({
  onDismiss,
  onAppliedTemplate,
  stepSetup,
  organizationType,
  setOnboardingState,
}: PersonalisedQuickActionsProps) {
  const isMobile = useIsMobile();
  const t = useTranslations('onboarding.onboarding_setup');

  const quickActionOptions = useQuickActionsOptions({
    organizationType,
  });

  const [createActionButtonState, setCreateActionButtonState] =
    useState<RagnarButtonStatus>();
  const [startOnMyOwnButtonState, setStartOnMyOwnButtonState] =
    useState<RagnarButtonStatus>();

  const [selectedQuickAction, setSelectedQuickAction] = useState<string>();
  const [hoverQuickAction, setHoverQuickAction] = useState<string>();

  const handleDismiss = useCallback(() => {
    trackOnboardingJourneyEvent({
      context: TrackingContext.OnboardingPage,
      step: 'Personalized Quick Actions',
      option: 'Empty presentation',
    });

    onDismiss();
  }, [onDismiss]);

  const handleError = useCallback(() => {
    if (selectedQuickAction) {
      setCreateActionButtonState('error');
    } else {
      setStartOnMyOwnButtonState('error');
    }

    handleDismiss();
  }, [selectedQuickAction, handleDismiss]);

  const handleSuccess = useCallback(
    (existingPresentationEditPath?: string) => {
      trackUser({
        event: 'Onboarding journey',
        properties: {
          step: 'Personalized Quick Actions',
          option: selectedQuickAction,
        },
      });
      onAppliedTemplate(existingPresentationEditPath);
    },
    [onAppliedTemplate, selectedQuickAction],
  );

  const handleSelectedOption = useCallback(
    (quickActionOption: string) => {
      setSelectedQuickAction(quickActionOption);
      setOnboardingState({ selectedQuickAction: quickActionOption });
      trackClickedQuickActionsOption(
        TrackingContext.OnboardingPage,
        quickActionOption,
      );

      if (typeof window.pyne === 'function') {
        const pyneEvent =
          `onboarding-quick-action-selected--${quickActionOption}` as const;
        window.pyne('trackEvent', pyneEvent);
      }
    },
    [setOnboardingState],
  );

  function handleHoverOption(quickActionOption: string | undefined): void {
    setHoverQuickAction(quickActionOption);
  }

  const step = stepSetup.getCurrentStepNumber(
    OnboardingStepEnum.personalizedQuickActions,
  );
  const totalSteps = stepSetup.totalSteps;

  const handleSubmit = useCallback(() => {
    // We don't want to trigger multiple submissions
    if (createActionButtonState === 'loading') return;
    if (startOnMyOwnButtonState === 'loading') return;
    // We don't want to trigger submits after it has been successful,
    // only on errors
    if (createActionButtonState === 'success') return;
    if (startOnMyOwnButtonState === 'success') return;
    if (!selectedQuickAction) return;

    const definition = quickActionOptions.find(
      ({ id }) => id === selectedQuickAction,
    );

    createPresentationFromQuickActionsDefinition({
      quickActionDefinition: definition!,
      onSuccess: (existingPresentationEditPath: string) => {
        handleSuccess(existingPresentationEditPath);
        if (
          definition?.id?.includes('wordcloud') ||
          definition?.id?.includes('poll')
        )
          LocalStorage.setItem({
            value: definition?.id?.includes('wordcloud') ? 'wordcloud' : 'poll',
            key: APPLIED_QUICK_ACTION_KEY,
            type: 'functional',
          });
      },
      onError: handleError,
    });

    setCreateActionButtonState('loading');
  }, [
    quickActionOptions,
    createActionButtonState,
    startOnMyOwnButtonState,
    selectedQuickAction,
    handleSuccess,
    handleError,
  ]);

  const handleSkip = useCallback(() => {
    setStartOnMyOwnButtonState('loading');
    handleDismiss();
  }, [handleDismiss]);

  const isLoading =
    (createActionButtonState &&
      ['loading', 'error'].includes(createActionButtonState)) ||
    startOnMyOwnButtonState === 'loading';

  const isSuccess =
    createActionButtonState === 'success' ||
    startOnMyOwnButtonState === 'success';

  const leftSidePanelContent =
    hoverQuickAction || selectedQuickAction ? (
      <VideoContent
        key={hoverQuickAction ?? selectedQuickAction}
        videoSrcUrl={getQuickActionVideoUrl(
          quickActionOptions,
          hoverQuickAction ?? selectedQuickAction,
        )}
      />
    ) : (
      <ImageContent imageSrc={QuickActionsAsset.src} />
    );

  return (
    <FullScreenOnboardingPageLayout
      // TODO hardcoded color to match video background that can differ due to video compression and processing
      backgroundColor="#f2ece8"
      sideShown={true}
      contentWidth="50vw"
      leftSidePanelContent={leftSidePanelContent}
      setOnboardingState={setOnboardingState}
      stepSetup={stepSetup}
      currentStep={OnboardingStepEnum.personalizedQuickActions}
    >
      <EaseIn
        flex="1 1 auto"
        width="100%"
        extend={() => ({ overflowX: 'hidden', overflowY: 'auto' })}
      >
        <Body
          step={step}
          totalSteps={totalSteps}
          width="100%"
          mt={['auto', 'auto', 'auto', 'auto']}
        >
          <Text as="h1" mb="space6" variant="dashboardHeadline3">
            {t('personalized_quick_actions_step.page_heading')}
          </Text>

          <Box
            alignItems={['flex-start', 'center']}
            width="100%"
            data-testid="onboarding-quick-actions-container-box"
          >
            <PersonalizedQuickActionsRadioGroup
              quickActionOptionList={quickActionOptions}
              selectedQuickAction={selectedQuickAction}
              isFormDisabled={isLoading}
              onSelectedOption={handleSelectedOption}
              onHoverOption={handleHoverOption}
            />
          </Box>
        </Body>

        <Footer>
          {!isMobile && (
            <StepsAndProgressBar step={step} totalSteps={totalSteps} />
          )}

          <FormActions
            actionText={t('create_button')}
            onAction={handleSubmit}
            disableAction={!selectedQuickAction || isLoading || isSuccess}
            actionLoadingState={createActionButtonState}
            skipText={t('explore_on_my_own_button')}
            onSkip={handleSkip}
            disableSkip={isLoading || isSuccess}
            skipLoadingState={startOnMyOwnButtonState}
          />
        </Footer>
      </EaseIn>
    </FullScreenOnboardingPageLayout>
  );
}
